// OWNER: platform module. Service worker registration (PWA, NFR "Offline"). Called from src/main.tsx.
//
// - Registers /sw.js (scope "/") in production builds. In development it is opt-in, so Vite's dev server and
//   hot reload aren't affected: set VITE_SW_DEV=true, or run `localStorage.setItem('st.swDev', '1')` in the
//   browser console and reload (remove the key and unregister in DevTools › Application to switch it off).
//   Never in tests/jsdom.
// - Other features (e.g. web push in the notifications area) use `navigator.serviceWorker.ready`.
// - When a new version has been installed and is waiting, `swUpdateStore` holds it; the OfflineBanner shows
//   "A new version of Seventide is available — Reload", which calls `applyServiceWorkerUpdate()`.
// - Messages from the worker: REPLAY_OUTBOX (Background Sync) → replay the outbox; NAVIGATE (notification
//   click when the window couldn't be navigated directly) → go to the same-origin path.
import { createStore, useStore } from '../../../lib/store';
import { readStorage } from '../../../lib/storage';
import { safeNotificationPath } from './notificationUrl';
import { offlineQueue } from './outbox';

export const SW_URL = '/sw.js';
export const SW_DEV_STORAGE_KEY = 'st.swDev';
const UPDATE_CHECK_MS = 60 * 60_000;

export interface SwUpdateState {
  /** A new worker is installed and waiting to take over. */
  waiting: ServiceWorker | null;
  /** The page asked it to take over and will reload. */
  reloading: boolean;
}

export const swUpdateStore = createStore<SwUpdateState>({ waiting: null, reloading: false });

export function useServiceWorkerUpdate(): SwUpdateState {
  return useStore(swUpdateStore);
}

interface SwEnv {
  PROD?: boolean;
  MODE?: string;
  VITE_SW_DEV?: string;
}

/** Whether to register: production, or development with the opt-in; never in tests or jsdom. */
export function shouldRegisterServiceWorker(
  env: SwEnv = import.meta.env as SwEnv,
  nav: Navigator | undefined = typeof navigator === 'undefined' ? undefined : navigator,
  devFlag: string | null = readStorage(SW_DEV_STORAGE_KEY),
): boolean {
  if (!nav || !('serviceWorker' in nav)) return false;
  if (env.MODE === 'test' || /jsdom/i.test(nav.userAgent ?? '')) return false;
  if (env.PROD) return true;
  return env.VITE_SW_DEV === 'true' || devFlag === '1';
}

function watchInstalling(reg: ServiceWorkerRegistration, worker: ServiceWorker | null) {
  if (!worker) return;
  worker.addEventListener('statechange', () => {
    // "installed" with an existing controller = an update waiting (first installs activate straight away).
    if (worker.state === 'installed' && navigator.serviceWorker.controller) {
      swUpdateStore.set((s) => ({ ...s, waiting: reg.waiting ?? worker }));
    }
  });
}

export function trackUpdates(reg: ServiceWorkerRegistration): void {
  if (reg.waiting && navigator.serviceWorker.controller) swUpdateStore.set((s) => ({ ...s, waiting: reg.waiting }));
  watchInstalling(reg, reg.installing);
  reg.addEventListener('updatefound', () => watchInstalling(reg, reg.installing));
}

/** Tells the waiting worker to take over; the page reloads once it controls the page. */
export function applyServiceWorkerUpdate(): void {
  const { waiting } = swUpdateStore.get();
  if (!waiting) {
    window.location.reload();
    return;
  }
  swUpdateStore.set((s) => ({ ...s, reloading: true }));
  waiting.postMessage({ type: 'SKIP_WAITING' });
}

/** Handles messages from the worker (exported for tests). */
export function handleWorkerMessage(data: unknown, reply?: MessagePort): void {
  const msg = (data ?? {}) as { type?: string; url?: unknown };
  if (msg.type === 'REPLAY_OUTBOX') {
    void offlineQueue.replay();
    reply?.postMessage({ ok: true });
  } else if (msg.type === 'NAVIGATE') {
    const path = safeNotificationPath(msg.url, window.location.origin);
    reply?.postMessage({ ok: true });
    window.location.assign(path);
  }
}

let registered = false;

export function registerServiceWorker(): void {
  if (registered || !shouldRegisterServiceWorker()) return;
  registered = true;
  const sw = navigator.serviceWorker;

  let reloadingForUpdate = false;
  sw.addEventListener('controllerchange', () => {
    // Only reload when the user chose "Reload" (not on the very first install, which claims the page).
    if (!swUpdateStore.get().reloading || reloadingForUpdate) return;
    reloadingForUpdate = true;
    window.location.reload();
  });
  sw.addEventListener('message', (event: MessageEvent) => handleWorkerMessage(event.data, event.ports?.[0]));

  const start = () => {
    sw.register(SW_URL, { scope: '/', updateViaCache: 'none' })
      .then((reg) => {
        trackUpdates(reg);
        // Look for a new version now and then, and when the app comes back to the foreground.
        window.setInterval(() => void reg.update().catch(() => {}), UPDATE_CHECK_MS);
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') void reg.update().catch(() => {});
        });
      })
      .catch(() => {
        // Registration can fail (private mode, blocked storage). The app works without it, just not offline.
      });
  };
  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start, { once: true });
}
